/**
 * Track 2B — Shared "What Happened Here" story drawer.
 *
 * One drawer, multiple surfaces. Renders an OperationalStory in the
 * same construction-language shape used by SheetOverlayMarkers, but
 * standalone so the War Room, Command View, Inbox, and Operations
 * Dashboard can all open it without reimplementing the UI.
 *
 * Usage:
 *    <StoryDrawer open story={storyObj} onClose={() => setOpen(false)} />
 *    <StoryDrawer open storyId="st_..." onClose={...} />
 *    <StoryDrawer open inboxItemId="fa_..." onClose={...} />
 *
 * Hooks-first, rules-of-hooks safe.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Camera, Mic, MessageSquareWarning, ShieldAlert, Send, ListChecks,
  CheckCircle2, ChevronRight, X, ExternalLink, Loader2, Clock, Users,
} from "lucide-react";
import { api } from "@/lib/api";


export const KIND = {
  photo:              { label: "Photo",            icon: Camera,               colour: "#0ea5e9" },
  voice_note:         { label: "Voice note",       icon: Mic,                  colour: "#10b981" },
  clarification_open: { label: "Clarification",    icon: MessageSquareWarning, colour: "#eab308" },
  field_conflict:     { label: "Field conflict",   icon: ShieldAlert,          colour: "#dc2626" },
  revision_send:      { label: "Sent to foreman",  icon: Send,                 colour: "#6366f1" },
  foreman_ack:        { label: "Acknowledged",     icon: CheckCircle2,         colour: "#16a34a" },
  foreman_complete:   { label: "Completed",        icon: ListChecks,           colour: "#475569" },
};

export const STATUS_TONE = {
  conflict_open:               "#dc2626",
  waiting_for_clarification:   "#eab308",
  waiting_for_field:           "#f59e0b",
  needs_attention:             "#f97316",
  waiting_for_review:          "#6366f1",
  in_progress:                 "#0ea5e9",
  resolved:                    "#16a34a",
  closed:                      "#475569",
};

const SEVERITY = ["field_conflict", "clarification_open", "revision_send",
                  "voice_note", "photo", "foreman_ack", "foreman_complete"];

export function topKind(story) {
  const kinds = new Set((story?.members || []).map((m) => m.kind));
  return SEVERITY.find((k) => kinds.has(k)) || "photo";
}


export default function StoryDrawer({
  open, story: providedStory, storyId, inboxItemId, sourceKind, sourceId,
  onClose,
}) {
  const [story, setStory] = useState(providedStory || null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [retryTick, setRetryTick] = useState(0);

  const targetKey = useMemo(
    () => providedStory?.id || storyId || (inboxItemId ? `i:${inboxItemId}` :
          (sourceKind && sourceId ? `${sourceKind}:${sourceId}` : "")),
    [providedStory, storyId, inboxItemId, sourceKind, sourceId],
  );

  // Resolve the story when opened (or when target changes).
  useEffect(() => {
    if (!open) return;
    if (providedStory) {
      setStory(providedStory);
      setError(null);
      return;
    }
    if (!storyId && !inboxItemId && !(sourceKind && sourceId)) {
      setError("No story target");
      setStory(null);
      return;
    }
    let alive = true;
    setLoading(true);
    setError(null);
    const url = storyId
      ? `/stories/${storyId}`
      : inboxItemId
        ? `/stories/by-source/inbox_item/${inboxItemId}`
        : `/stories/by-source/${sourceKind}/${sourceId}`;
    api.get(url)
      .then((r) => { if (alive) { setStory(r.data); } })
      .catch((e) => {
        if (!alive) return;
        const detail = e?.response?.data?.detail;
        setError(detail || "Couldn't open the story");
        setStory(null);
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, providedStory, storyId, inboxItemId, sourceKind, sourceId, targetKey, retryTick]);

  // Refresh when the engine broadcasts.
  useEffect(() => {
    if (!open || !story?.id) return;
    const h = async () => {
      try {
        const r = await api.get(`/stories/${story.id}`);
        setStory(r.data);
      } catch { /* the story may have been merged into another */ }
    };
    window.addEventListener("forgedops:field-stories-changed", h);
    return () => window.removeEventListener("forgedops:field-stories-changed", h);
  }, [open, story?.id]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-y-0 right-0 w-full sm:w-[440px] bg-[var(--fop-surface)] border-l border-[var(--fop-border)] z-50 flex flex-col"
      data-testid="story-drawer"
      style={{ pointerEvents: "auto" }}
    >
      <header className="px-4 py-2 border-b border-[var(--fop-border)] flex items-center gap-2">
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--fop-primary)]">
          What Happened Here
        </div>
        <button
          onClick={onClose} aria-label="Close" data-testid="story-drawer-close"
          className="ml-auto text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"
        ><X size={14} /></button>
      </header>
      {loading && (
        <div className="p-6 text-sm text-[var(--fop-text-3)] flex items-center gap-2" data-testid="story-drawer-loading">
          <Loader2 className="animate-spin" size={14} /> Loading the story…
        </div>
      )}
      {!loading && error && (
        <div className="p-6 text-sm text-[var(--fop-text-3)] space-y-3" data-testid="story-drawer-empty">
          <div>{error}</div>
          {!providedStory && (
            <button
              type="button"
              onClick={() => setRetryTick((t) => t + 1)}
              className="font-mono text-[10px] uppercase tracking-wider px-2 py-1 rounded border border-[var(--fop-border)] hover:bg-[var(--fop-surface-2)]"
              data-testid="story-drawer-retry"
            >
              Retry
            </button>
          )}
        </div>
      )}
      {!loading && story && <StoryBody story={story} onClose={onClose} />}
    </div>
  );
}


function StoryBody({ story, onClose }) {
  const top = topKind(story);
  const meta = KIND[top] || KIND.photo;
  const TopIcon = meta.icon;
  const statusColour = STATUS_TONE[story.status] || meta.colour;
  return (
    <div className="flex-1 overflow-y-auto p-4" data-testid="story-drawer-body">
      <div className="flex items-start gap-2 mb-3">
        <div
          className="mt-0.5 flex items-center justify-center rounded-full"
          style={{ width: 28, height: 28, background: "rgba(255,255,255,0.94)",
                   border: `2px solid ${meta.colour}66`, color: meta.colour }}
        >
          <TopIcon size={14} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-[var(--fop-text)] text-base truncate" data-testid="story-drawer-title">
            {meta.label} on {story.sheet_number || "this sheet"}
          </div>
          <div className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-[0.18em]"
               style={{ background: "rgba(0,0,0,0.04)", color: statusColour, border: `1px solid ${statusColour}55` }}
               data-testid="story-drawer-status">
            {story.status_label || story.status || "Needs Attention"}
          </div>
        </div>
      </div>

      <dl className="text-[12px] grid grid-cols-[110px_1fr] gap-y-1 mb-3">
        <Row k="Sheet"       v={story.sheet_number || story.sheet_id} />
        <Row k="First seen"  v={_fmt(story.started_at)} />
        <Row k="Last update" v={_fmt(story.last_activity_at)} />
      </dl>

      {(story.people || []).length > 0 && (
        <Section icon={<Users size={11} />} label="People involved" testid="story-drawer-people">
          <div className="flex flex-wrap gap-1">
            {story.people.map((p) => (
              <span key={p} className="text-[11px] px-1.5 py-0.5 rounded bg-[var(--fop-primary-soft)]/40 text-[var(--fop-text-2)] border border-[var(--fop-border)]">{p}</span>
            ))}
          </div>
        </Section>
      )}

      <Section icon={<Clock size={11} />} label="Related Activity" testid="story-drawer-related">
        <ol className="space-y-1.5 border-l-2 border-[var(--fop-border)] pl-3">
          {(story.members || []).map((m, idx) => {
            const km = KIND[m.kind] || KIND.photo;
            const Icon = km.icon;
            const stage = idx === 0 ? "First"
              : idx === ((story.members || []).length - 1) ? "Now"
              : (idx === 1 ? "Next" : "After that");
            return (
              <li key={`${m.kind}_${m.id}_${idx}`}
                  data-testid={`story-drawer-activity-${m.kind}-${m.id}`}
                  className="grid grid-cols-[12px_1fr] gap-2 items-start">
                <span className="-ml-[19px] mt-0.5 inline-flex items-center justify-center rounded-full"
                      style={{ width: 14, height: 14, background: "var(--fop-surface)", color: km.colour, border: `2px solid ${km.colour}66` }}>
                  <Icon size={8} />
                </span>
                <div className="text-[12px] text-[var(--fop-text)]">
                  <div className="flex items-center gap-1">
                    <span className="font-mono text-[9px] uppercase tracking-wider text-[var(--fop-text-3)]">{stage}</span>
                    <span className="font-semibold text-[var(--fop-text)]">{km.label}</span>
                    {m.actor && <span className="text-[var(--fop-text-3)]">· {m.actor}</span>}
                  </div>
                  {m.title && <div className="text-[var(--fop-text-2)] truncate">{m.title}</div>}
                  {m.reason && <div className="text-[10px] text-[var(--fop-text-3)] italic">Linked: {m.reason}</div>}
                </div>
              </li>
            );
          })}
        </ol>
      </Section>

      {(story.open_actions || []).length > 0 && (
        <Section icon={<ChevronRight size={11} />} label="Still needs attention" testid="story-drawer-open-actions">
          <ul className="text-[12px] text-[var(--fop-text)] space-y-0.5">
            {story.open_actions.map((a, i) => (
              <li key={`open_${i}`} data-testid={`story-drawer-open-${a.kind}`} className="flex items-center gap-1">
                <ChevronRight size={11} className="text-[var(--fop-text-3)]" />
                {a.label}{a.assignee_name ? ` · ${a.assignee_name}` : ""}
              </li>
            ))}
          </ul>
        </Section>
      )}

      {(story.closed_actions || []).length > 0 && (
        <Section icon={<CheckCircle2 size={11} />} label="Closed" testid="story-drawer-closed-actions">
          <ul className="text-[12px] text-[var(--fop-text-2)] space-y-0.5">
            {story.closed_actions.map((a, i) => (
              <li key={`closed_${i}`} className="flex items-center gap-1">
                <CheckCircle2 size={11} className="text-[var(--fop-success)]" />
                {a.label}{a.assignee_name ? ` · ${a.assignee_name}` : ""}
              </li>
            ))}
          </ul>
        </Section>
      )}

      <div className="flex gap-3 flex-wrap pt-1 border-t border-[var(--fop-border)] mt-2">
        <a
          href={`/sheet/${story.sheet_id}`}
          onClick={onClose}
          className="text-[12px] text-[var(--fop-text-2)] hover:text-[var(--fop-text)] inline-flex items-center gap-1"
          data-testid="story-drawer-jump-drawing"
        >
          <ExternalLink size={11} /> Jump to drawing
        </a>
        <a
          href={`/sheets/${story.sheet_id}/field-timeline`}
          onClick={onClose}
          className="text-[12px] text-[var(--fop-text-2)] hover:text-[var(--fop-text)] inline-flex items-center gap-1"
          data-testid="story-drawer-open-timeline"
        >
          <ExternalLink size={11} /> Open Field Timeline
        </a>
      </div>
    </div>
  );
}


function Section({ icon, label, testid, children }) {
  return (
    <section className="mb-3" data-testid={testid}>
      <div className="flex items-center gap-1 mb-1 font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--fop-text-3)]">
        {icon}{label}
      </div>
      {children}
    </section>
  );
}


function Row({ k, v }) {
  if (!v && v !== 0) return null;
  return (
    <>
      <dt className="font-mono text-[10px] uppercase tracking-wider text-[var(--fop-text-3)] pt-0.5">{k}</dt>
      <dd className="text-[var(--fop-text-2)] truncate">{v}</dd>
    </>
  );
}


function _fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return String(iso); }
}
